export interface TwilioError {
    code: number;
    message: string;
    twilioError?: {
        code: number;
        causes: string[];
        description: string;
        explanation: string;
        solutions: string[];
        message: string;
        stack: string;
        originalError?: Error;
    };
}

// https://www.twilio.com/docs/api/errors
const ACCESS_TOKEN_INVALID = 20101;
const ACCESS_TOKEN_EXPIRED = 20104;
const ACCESS_TOKEN_NOT_YET_VALID = 20105;
const ACCESS_TOKEN_INVALID_SIGNATURE = 20107;

export const twilioTokenErrorCodes = [
    ACCESS_TOKEN_INVALID,
    ACCESS_TOKEN_EXPIRED,
    ACCESS_TOKEN_NOT_YET_VALID,
    ACCESS_TOKEN_INVALID_SIGNATURE,
];

//https: www.twilio.com/docs/voice/sdks/error-codes
export const GENERAL_ERROR = 31000;
export const CONNECTION_DECLINED = 31002;
export const POOR_INTERNET_CONNECTION = 31003;
export const NO_INTERNET = 31005;
export const INVALID_CONTACT_NAME = 31105;
export const MICROPHONE_NOT_FOUND = 31201;
export const INVALID_JWT = 31204;
export const EXPIRED_JWT = 31205;
export const AUTHENTICATION_EXPIRED = 31207;
export const MICROPHONE_NOT_ENABLED = 31208;
export const NO_TRANSPORT_AVAILABLE = 31009;

export const twilioNoisyErrorsToIgnore = [NO_TRANSPORT_AVAILABLE, NO_INTERNET, GENERAL_ERROR];

// Twilio Sync errors reach the UI through @twilio/conversations, which keeps every conversation
// in a Sync document, a messages list and a participants map.
// https://www.twilio.com/docs/api/errors
const SYNC_DOCUMENT_NOT_FOUND = 54100;
const SYNC_LIST_NOT_FOUND = 54150;
const SYNC_MAP_NOT_FOUND = 54200;
const SYNC_UNIQUE_NAME_NOT_FOUND = 54300;

const NOT_FOUND_STATUS = 404;

export const twilioSyncMissingResourceCodes = [
    SYNC_DOCUMENT_NOT_FOUND,
    SYNC_LIST_NOT_FOUND,
    SYNC_MAP_NOT_FOUND,
    SYNC_UNIQUE_NAME_NOT_FOUND,
];

type TwilioSyncErrorShape = {
    status?: unknown;
    code?: unknown;
};

export function getTwilioErrorDetails(error: unknown): { status?: number; code?: number } {
    if (typeof error !== "object" || error === null) {
        return {};
    }
    const { status, code } = error as TwilioSyncErrorShape;
    return {
        status: typeof status === "number" ? status : undefined,
        code: typeof code === "number" ? code : undefined,
    };
}

/**
 * A conversation that was deleted in Twilio can linger in the client's subscription list, so the
 * SDK keeps asking Sync for entities that no longer exist. `SyncError` derives its `name` from
 * `this.constructor.name`, which minifies away in production builds, so match on status and code.
 */
export function isMissingTwilioSyncResourceError(error: unknown): boolean {
    const { status, code } = getTwilioErrorDetails(error);
    return status === NOT_FOUND_STATUS && code !== undefined && twilioSyncMissingResourceCodes.includes(code);
}

/**
 * Sync errors that escape the SDK as unhandled rejections only reach Datadog as a formatted
 * message, for example `Unique name not found (status: 404, code: 54300)`.
 */
export function isMissingTwilioSyncResourceMessage(message: string): boolean {
    return twilioSyncMissingResourceCodes.some((code) =>
        message.includes(`(status: ${NOT_FOUND_STATUS}, code: ${code})`)
    );
}

export const twillioVoiceErrorCodeMappings: Record<number, string> = {
    [GENERAL_ERROR]: "General Error encountered",
    [CONNECTION_DECLINED]: "Connection Declined",
    [POOR_INTERNET_CONNECTION]: "Poor or intermittent Internet connection",
    [NO_INTERNET]: "No internet",
    [INVALID_CONTACT_NAME]: "Invalid contact name",
    [MICROPHONE_NOT_FOUND]: "Microphone not found",
    [INVALID_JWT]: "Invalid authentication token. Please logout and login again",
    [EXPIRED_JWT]: "Authentication expired. Please logout and login again",
    [AUTHENTICATION_EXPIRED]: "Authentication expired. Please logout and login again",
    [MICROPHONE_NOT_ENABLED]: "Microphone has not been enabled",
    [NO_TRANSPORT_AVAILABLE]: "No transport available to send or receive messages",
};
